import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'

import config from '@app/config'

import type { ApiDepersonalizedAnnouncement, ApiInaccurateLocation } from '@app/packages/api/v2/types/entities'

import moment from './moment'

export class JobPosting {
  private formatMessage: IntlShape['formatMessage']
  private locations: Record<string, ApiInaccurateLocation>

  constructor({ formatMessage, locations }: { formatMessage: IntlShape['formatMessage']; locations: Record<string, ApiInaccurateLocation> }) {
    this.formatMessage = formatMessage
    this.locations = locations
  }

  create(announcement: ApiDepersonalizedAnnouncement) {
    const location = this.getLocation(announcement)

    return {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title: this.formatMessage(messages.nanny_babysitter),
      description: this.announcementDescription(announcement),
      datePosted: moment(announcement.attributes.created_at).format('YYYY-MM-DD'),
      validThrough: moment.unix(announcement.attributes.when.begin).format('YYYY-MM-DD'),
      hiringOrganization: {
        '@type': 'Organization',
        name: 'Kidsout',
        sameAs: config.webUrl,
        logo: `${config.webUrl}/icon-512.png`,
      },
      ...(location
        ? {
            jobLocation: {
              '@type': 'Place',
              address: {
                '@type': 'PostalAddress',
                streetAddress: location.attributes.street,
                addressLocality: location.attributes.city_name,
                addressCountry: location.attributes.country_name,
              },
            },
          }
        : undefined),
      ...(announcement.attributes.online ? { jobLocationType: 'TELECOMMUTE' } : undefined),
      employmentType: 'PART_TIME',
      identifier: {
        '@type': 'PropertyValue',
        name: 'Kidsout',
        value: announcement.id,
      },
      ...(announcement.attributes.recommended_price
        ? {
            baseSalary: {
              '@type': 'MonetaryAmount',
              currency: announcement.attributes.recommended_price_currency,
              value: {
                '@type': 'QuantitativeValue',
                value: String(Math.round((announcement.attributes.recommended_price / (this.announcementDuration(announcement) / 60)) * 100) / 100),
                unitText: 'HOUR',
              },
            },
          }
        : undefined),
    }
  }

  private getLocation(announcement: ApiDepersonalizedAnnouncement) {
    const id = announcement.relationships.location.data?.id
    if (!id) return null
    return this.locations[id] ?? null
  }

  /**
   * @returns Duration in minutes
   */
  private announcementDuration(ann: ApiDepersonalizedAnnouncement) {
    const start = moment.unix(ann.attributes.when.begin)
    const end = moment.unix(ann.attributes.when.end)
    const diff = end.diff(start, 'minutes')
    return diff
  }

  private announcementDescription(ann: ApiDepersonalizedAnnouncement) {
    const now = moment()
    const childrenAges = ann.attributes.kids_data.map(k => {
      const date = moment(`${k.year}-${k.month}`, 'YYYY-MM')
      const age = now.diff(date, 'years') || 1
      return age
    })
    const ch = childrenAges.length < 2 ? this.formatMessage(messages.children_one) : this.formatMessage(messages.children_many)
    const ages = childrenAges
      .map((age, index, arr) => (index === arr.length - 1 ? this.formatMessage(messages.age, { age }) : String(age)))
      .join(this.formatMessage(messages.and))
    const chlabel = `${ch} ${ages}`
    const desc = this.formatMessage(messages.need_nanny, {
      duration: Math.ceil(this.announcementDuration(ann) / 60),
      children: chlabel,
    })
    return desc
  }
}

const messages = defineMessages({
  nanny_babysitter: { id: "utils.JobPosting.messages.nanny_babysitter.0a16", defaultMessage: 'Няня / Бебиситтер' },
  need_nanny: { id: "utils.JobPosting.messages.need_nanny.1d8a", defaultMessage: 'Требуется бебиситтер на {duration} {duration, plural, one {час} few {часа} other {часов}} для {children}' },
  children_one: { id: "utils.JobPosting.messages.children_one.3b0b", defaultMessage: 'ребенка' },
  children_many: { id: "utils.JobPosting.messages.children_many.2c3b", defaultMessage: 'детей' },
  age: { id: "utils.JobPosting.messages.age.6909", defaultMessage: '{age} {age, plural, one {года} other {лет}}' },
  and: { id: "utils.JobPosting.messages.and.7336", defaultMessage: ' и ' },
})
